import { Link } from "react-router-dom";
import type { Movie } from "@/lib/types";
import { TYPE_LABELS } from "@/lib/media";

interface DetailIntroProps {
  movie: Movie;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onShare: () => void;
}

export default function DetailIntro({ movie, isFavorite, onToggleFavorite, onShare }: DetailIntroProps) {
  return (
    <section className="relative overflow-hidden bg-foreground-950" aria-label="作品基本信息">
      <div className="absolute inset-0">
        <img
          src={movie.backdrop ?? movie.poster}
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover object-top opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950 via-foreground-950/85 to-foreground-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-transparent to-foreground-950/60" />
      </div>

      <div className="shell relative z-10 py-7 md:py-11">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-background-300" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-background-50">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm opacity-60" />
          <Link to={`/category/${movie.type}`} className="transition-colors hover:text-background-50">
            {TYPE_LABELS[movie.type]}
          </Link>
          <i className="ri-arrow-right-s-line text-sm opacity-60" />
          <span className="text-background-100">{movie.title}</span>
        </nav>

        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-[150px_1fr] md:mt-7 md:gap-7 lg:grid-cols-[190px_1fr]">
          <div className="w-[130px] overflow-hidden rounded-md border border-background-50/15 sm:w-full">
            <div className="aspect-[2/3] w-full bg-secondary-800">
              <img
                src={movie.poster}
                alt={`《${movie.title}》海报`}
                title={`${movie.title} 剧情介绍与演员名单`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-accent-500 px-2 py-0.5 text-[11px] font-bold text-foreground-950">
                {TYPE_LABELS[movie.type]}
              </span>
              <span className="rounded-md border border-background-50/25 px-2 py-0.5 text-[11px] font-medium text-background-200">
                {movie.status}
              </span>
              <span className="text-[11px] text-background-300">更新于 {movie.updatedAt}</span>
            </div>

            <h1 className="mt-3 font-heading text-3xl font-extrabold leading-tight tracking-tight text-background-50 md:text-[40px]">
              {movie.title}
            </h1>
            <p className="mt-1 font-label text-xs uppercase tracking-[0.2em] text-background-400">
              {movie.enTitle}
            </p>

            <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-background-200 md:text-sm">
              <span className="flex items-center gap-1 rounded-md bg-accent-500/95 px-2 py-1 font-bold text-foreground-950">
                <i className="ri-star-fill text-xs" />
                {movie.rating.toFixed(1)}
              </span>
              <span>{movie.year}</span>
              <span className="h-3 w-px bg-background-50/25" />
              <span>{movie.region}</span>
              <span className="h-3 w-px bg-background-50/25" />
              <span>{movie.duration}</span>
              <span className="h-3 w-px bg-background-50/25" />
              <span>导演 {movie.director}</span>
            </div>

            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200 md:text-[15px]">
              {movie.tagline}
            </p>

            <div className="mt-3 flex flex-wrap gap-1.5">
              {movie.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/category/${movie.type}`}
                  className="rounded-md border border-background-50/20 px-2 py-0.5 text-[11px] text-background-300 transition-colors hover:border-accent-400 hover:text-accent-300"
                >
                  {tag}
                </Link>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2.5">
              <button
                type="button"
                onClick={onToggleFavorite}
                className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
                  isFavorite
                    ? "bg-accent-500 text-foreground-950 hover:bg-accent-400"
                    : "bg-background-50 text-foreground-950 hover:bg-background-200"
                }`}
              >
                <i className={isFavorite ? "ri-heart-fill text-base" : "ri-heart-line text-base"} />
                {isFavorite ? "已收藏" : "收藏作品"}
              </button>
              <button
                type="button"
                onClick={onShare}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-share-forward-line text-base" />
                分享
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}